import { FlexBox, ItemSelector } from '@airship-platform/jam-web/components';
import { PlusIcon } from '@airship-platform/jam-web/icons';
import { EmailValidationSchema } from '@airship-platform/common/validation';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';

const MAX_CONTACTS_VISIBLE = 12;

export const EmailsInputSchema = z.object({
  emailFilter: z.string(),
  emails: EmailValidationSchema.array().max(
    5,
    'You can only add up to 5 email addresses'
  ),
});

export type EmailsInputValues = z.infer<typeof EmailsInputSchema>;

interface EmailsInputProps {
  emailContacts: string[];
  isLoading: boolean;
}

const EmailsInput: React.FC<EmailsInputProps> = ({
  emailContacts,
  isLoading,
}) => {
  const [customEmails, setCustomEmails] = useState<string[]>([]);

  const { control, formState, watch, setValue, clearErrors, setError } =
    useFormContext<EmailsInputValues>();

  const [emailFilter, selectedEmails] = watch(['emailFilter', 'emails']);

  const handleAddContact = (email: string) => {
    const newEmail = EmailValidationSchema.safeParse(email);

    if (!newEmail.success) {
      setError('emailFilter', {
        type: 'manual',
        message: 'Please enter a valid email address',
      });
      return;
    }

    if (
      emailContacts.includes(newEmail.data) ||
      customEmails.includes(newEmail.data)
    ) {
      setError('emailFilter', {
        type: 'manual',
        message: 'This email address is already added',
      });
      return;
    }

    setCustomEmails((ce) => [...ce, newEmail.data]);
    clearErrors('emailFilter');
    setValue('emailFilter', '');
    setValue('emails', [...selectedEmails, newEmail.data]);
  };

  const buildEmailInputHelperText = (
    error: string | undefined,
    value: string
  ) => {
    if (error) {
      return error;
    }

    if (value) {
      return 'Press enter or click the plus icon to add email';
    }

    const contactsCount = emailContacts.length;
    const contactsText = contactsCount === 1 ? 'contact' : 'contacts';
    const selectedCount = selectedEmails.length;
    const selectedText = selectedCount === 1 ? 'email' : 'emails';

    return selectedEmails.length
      ? `${selectedCount} ${selectedText} selected`
      : `Select from the ${contactsCount} ${contactsText} or start typing to filter or add a new one`;
  };

  const emailContactList = Array.from(
    new Set([...customEmails, ...emailContacts])
  );

  return (
    <FlexBox flexDirection="column" gap={2} width="100%">
      <Controller
        name="emailFilter"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleAddContact(field.value)}>
                      <PlusIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                onKeyDown: (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddContact(field.value);
                  }
                },
              },
            }}
            error={!!(fieldState.error || formState.errors.emails)}
            helperText={buildEmailInputHelperText(
              fieldState.error?.message || formState.errors.emails?.message,
              field.value
            )}
            placeholder="Search or add email address"
          />
        )}
      />

      <Controller
        name="emails"
        control={control}
        render={({ field }) => (
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            }}
          >
            {emailContactList
              .filter(
                (email) =>
                  email.toLowerCase().includes(emailFilter.toLowerCase()) ||
                  field.value.includes(email)
              )
              .slice(0, MAX_CONTACTS_VISIBLE)
              .map((email) => (
                <ItemSelector
                  key={email}
                  label={email}
                  value={email}
                  checked={field.value.includes(email)}
                  disabled={isLoading}
                  type="checkbox"
                  fullWidth
                  handleChange={(e) => {
                    clearErrors('emailFilter');
                    return field.onChange(
                      e.target.checked
                        ? [...field.value, email]
                        : field.value.filter((em) => em !== email)
                    );
                  }}
                />
              ))}
          </Box>
        )}
      />
    </FlexBox>
  );
};

export default EmailsInput;
